/* №3 «Төлбөр авах» — QPay (wire.mn QR) + шилжүүлэг/карт/бэлэн. Хөнгөлөлтгүй. */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { cwApi, type CwQpayStartResponse } from '@/api/carwash';
import { CAR_TYPES } from '@/shared/data';
import { tug } from '@/shared/format';
import { IconBank, IconBanknote, IconCard, IconCheck, IconLoader, IconQr, IconX } from '@/shared/icons';
import type { QueueCar } from '@/shared/types';
import { useToast } from '@/app/providers/ToastProvider';

/* method = backend CwPaymentMethod int. Дараалал: QPay, Шилжүүлэг, Карт, Бэлэн. */
const PAY_METHODS = [
  { key: 'QPay',       method: 3, Icon: IconQr },
  { key: 'Шилжүүлэг',  method: 2, Icon: IconBank },
  { key: 'Карт',       method: 1, Icon: IconCard },
  { key: 'Бэлэн',      method: 0, Icon: IconBanknote },
];

/* Псевдо QR placeholder (и-баримтын success дэлгэцэд) */
function FakeQR({ seed, size = 140 }: { seed: number; size?: number }) {
  const n = 17;
  let s = (seed % 2147483646) + 1;
  const rnd = () => ((s = (s * 16807) % 2147483647) / 2147483647);
  const cells: boolean[] = [];
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) {
      const f = (fx: number, fy: number) => {
        const lx = x - fx, ly = y - fy;
        if (lx < 0 || lx > 4 || ly < 0 || ly > 4) return null;
        return lx === 0 || lx === 4 || ly === 0 || ly === 4 || (lx === 2 && ly === 2);
      };
      const fin = f(0, 0) ?? f(n - 5, 0) ?? f(0, n - 5);
      cells.push(fin !== null ? fin : rnd() > 0.55);
    }
  }
  return (
    <div className="bg-white p-3 rounded-[10px] ring-1 ring-slate-200" style={{ width: size + 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${n}, 1fr)`, width: size, height: size }}>
        {cells.map((c, i) => (
          <div key={i} style={{ background: c ? '#0f172a' : 'transparent' }}></div>
        ))}
      </div>
    </div>
  );
}

function PaySectionLabel({ children }: { children: ReactNode }) {
  return <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{children}</div>;
}

export interface PaymentModalProps {
  open: boolean;
  car: QueueCar | null;
  mobile: boolean;
  onClose: () => void;
  onFinalize: (car: QueueCar, paidTotal: number) => void;
}

export function PaymentModal({ open, car, mobile, onClose, onFinalize }: PaymentModalProps) {
  const toast = useToast();
  const [methodIdx, setMethodIdx] = useState(0);
  const [ebarimt, setEbarimt] = useState<'Хувь хүн' | 'Байгууллага'>('Хувь хүн');
  const [rd, setRd] = useState('');
  const [phase, setPhase] = useState<'form' | 'qpay' | 'success'>('form');
  const [paidTotal, setPaidTotal] = useState(0);
  const [qpay, setQpay] = useState<CwQpayStartResponse | null>(null);
  const [qpayNote, setQpayNote] = useState<string | null>(null);
  const checkingRef = useRef(false);

  useEffect(() => {
    if (open) {
      setMethodIdx(0); setEbarimt('Хувь хүн'); setRd('');
      setPhase('form'); setPaidTotal(0); setQpay(null); setQpayNote(null);
    }
  }, [open]);

  const ebarimtBody = {
    EbarimtType: ebarimt === 'Байгууллага' ? 1 : 0,
    EbarimtRd: ebarimt === 'Байгууллага' ? rd : null,
  };

  const payMut = useMutation({
    mutationFn: (vars: { id: string; method: number }) =>
      cwApi.pay(vars.id, {
        DiscountType: 0,
        DiscountValue: 0,
        PaymentMethod: vars.method,
        ...ebarimtBody,
      }),
    onSuccess: (dto) => {
      setPaidTotal(dto.Total);
      setPhase('success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Алдаа гарлаа.'),
  });

  const qpayStartMut = useMutation({
    mutationFn: (id: string) => cwApi.qpayStart(id, ebarimtBody),
    onSuccess: (res) => {
      setQpay(res);
      setQpayNote(null);
      setPhase('qpay');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'Алдаа гарлаа.'),
  });

  /* QPay төлбөрийг шалгах — гар товч + 2 секундын автомат poll хоёул үүгээр */
  const checkQpay = async () => {
    if (!car || checkingRef.current) return;
    checkingRef.current = true;
    try {
      const res = await cwApi.qpayCheck(car.id);
      if (res.Paid) {
        setPaidTotal(res.Order?.Total ?? qpay?.Amount ?? car.subtotal);
        setPhase('success');
      } else if (res.Status === 'canceled') {
        setQpayNote('Нэхэмжлэх цуцлагдсан — өөр аргаар төлнө үү.');
      }
    } catch (e) {
      setQpayNote(e instanceof Error ? e.message : 'Шалгахад алдаа гарлаа.');
    } finally {
      checkingRef.current = false;
    }
  };

  useEffect(() => {
    if (!open || phase !== 'qpay' || qpayNote === 'Нэхэмжлэх цуцлагдсан — өөр аргаар төлнө үү.') return;
    const id = setInterval(() => void checkQpay(), 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, car?.id, qpayNote]);

  if (!car) return null;

  const total = car.subtotal;
  const rdOk = ebarimt === 'Хувь хүн' || /^\d{7}$/.test(rd);
  const busy = payMut.isPending || qpayStartMut.isPending;
  const canConfirm = rdOk && !busy;
  const selected = PAY_METHODS[methodIdx];
  const TypeIcon = CAR_TYPES[car.type];

  const confirm = () => {
    if (!canConfirm) return;
    if (selected.method === 3) qpayStartMut.mutate(car.id);
    else payMut.mutate({ id: car.id, method: selected.method });
  };

  /* --- Баримтын тал --- */
  const receipt = (
    <div className={'flex flex-col gap-3 rounded-[calc(var(--r)-2px)] bg-slate-50 ring-1 ring-slate-100 p-4 ' + (mobile ? '' : 'h-full')}>
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center border-[1.5px] border-slate-800 bg-white text-slate-900 font-extrabold tracking-wider leading-none rounded-[7px] text-[15px] px-2 py-[3px] whitespace-nowrap">{car.plate}</span>
        <span className="inline-flex items-center gap-1 text-[12px] text-slate-500 font-medium">
          <TypeIcon size={14} className="text-slate-400" />
          {car.type}
        </span>
        <span className="ml-auto text-[12px] text-slate-400">Угаагч: {car.washerNames}</span>
      </div>

      <div className="flex flex-col divide-y divide-slate-200/70">
        {car.lines.map((line, i) => (
          <div key={i} className="flex items-baseline justify-between py-2">
            <span className="text-[13px] text-slate-700">{line.name}</span>
            <span className="text-[13px] font-semibold text-slate-900">{tug(line.price)}</span>
          </div>
        ))}
      </div>

      <div className="mt-auto border-t border-slate-200 pt-3 flex items-baseline justify-between">
        <span className="text-[13px] font-bold text-slate-500">Нийт дүн</span>
        <span className="text-[26px] font-extrabold text-slate-900 tracking-tight">{tug(total)}</span>
      </div>
    </div>
  );

  /* --- Төлбөрийн тал --- */
  const payside = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <PaySectionLabel>Төлбөрийн арга</PaySectionLabel>
        <div className="grid grid-cols-4 gap-1.5">
          {PAY_METHODS.map(({ key, Icon }, idx) => {
            const sel = methodIdx === idx;
            return (
              <button key={key} onClick={() => setMethodIdx(idx)}
                className={
                  'h-[72px] rounded-[calc(var(--r)-2px)] flex flex-col items-center justify-center gap-1.5 px-1 text-[12px] font-semibold ring-1 transition leading-tight text-center ' +
                  (sel ? 'bg-cyan-50 ring-2 ring-[var(--brand2)] text-[var(--brand1)]' : 'bg-white ring-slate-200 text-slate-500 hover:ring-slate-300')
                }>
                <Icon size={22} strokeWidth={sel ? 2.2 : 1.8} />
                {key}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <PaySectionLabel>И-баримт</PaySectionLabel>
        <div className="grid grid-cols-2 gap-1 bg-slate-100 rounded-[12px] p-1">
          {(['Хувь хүн', 'Байгууллага'] as const).map((k) => (
            <button key={k} onClick={() => setEbarimt(k)}
              className={'min-h-[40px] rounded-[9px] text-[13px] font-semibold transition ' + (ebarimt === k ? 'bg-white text-slate-900 shadow-xs' : 'text-slate-500')}>
              {k}
            </button>
          ))}
        </div>
        {ebarimt === 'Байгууллага' && (
          <input value={rd} inputMode="numeric"
            onChange={(e) => setRd(e.target.value.replace(/\D/g, '').slice(0, 7))}
            placeholder="РД — 7 оронтой"
            className="w-full h-12 px-4 rounded-[calc(var(--r)-2px)] bg-slate-50 ring-1 ring-slate-200 text-[16px] font-bold tracking-[0.2em] text-slate-900 placeholder:text-slate-300 placeholder:font-medium placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-[var(--brand2)] focus:bg-white transition" />
        )}
      </div>

      <div className="mt-auto flex flex-col items-stretch gap-1.5">
        <button onClick={confirm} disabled={!canConfirm}
          className={
            'inline-flex items-center justify-center gap-2 min-h-[52px] px-6 rounded-[var(--r)] text-[15px] font-semibold transition active:scale-[0.98] ' +
            (canConfirm
              ? 'text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] shadow-md shadow-cyan-600/25 hover:opacity-95'
              : 'text-slate-400 bg-slate-100 cursor-not-allowed')
          }>
          <IconCheck size={18} strokeWidth={2.5} />
          {busy
            ? (qpayStartMut.isPending ? 'Нэхэмжлэх үүсгэж байна…' : 'Баталгаажуулж байна…')
            : selected.method === 3 ? 'QPay нэхэмжлэх үүсгэх' : 'Төлбөр баталгаажуулах'}
        </button>
        {!rdOk && <div className="text-[11px] text-slate-400 text-center">РД оруулна уу (7 оронтой)</div>}
      </div>
    </div>
  );

  /* --- QPay QR --- */
  const qpayView = qpay && (
    <div className="flex flex-col items-center text-center gap-3 py-2">
      <div className="text-[15px] font-bold text-slate-900">QPay-ээр төлөх</div>
      {qpay.QrImage ? (
        <img src={qpay.QrImage} alt="QPay QR" className="w-[230px] h-[230px] rounded-[12px] ring-1 ring-slate-200 bg-white" />
      ) : (
        <div className="w-[230px] h-[230px] rounded-[12px] ring-1 ring-slate-200 bg-slate-50 flex items-center justify-center text-[12px] text-slate-400 px-4 break-all">
          {qpay.QrText}
        </div>
      )}
      <div className="text-[26px] font-extrabold text-slate-900 tracking-tight leading-none">{tug(qpay.Amount)}</div>
      <div className="text-[12px] text-slate-400">Банкны аппаараа QR кодыг уншуулна уу</div>

      <div className="flex items-center gap-1.5 text-[12px] text-slate-400">
        <IconLoader size={13} className="animate-spin text-[var(--brand2)]" />
        Төлөлт автоматаар шалгагдаж байна…
      </div>
      {qpayNote && (
        <div className="text-[13px] font-medium text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-[10px] px-3.5 py-2.5">
          {qpayNote}
        </div>
      )}
      <div className="flex gap-2">
        <button onClick={() => { setPhase('form'); setQpayNote(null); }}
          className="min-h-[44px] px-5 rounded-[calc(var(--r)-4px)] text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 transition">
          Буцах
        </button>
        <button onClick={() => void checkQpay()}
          className="inline-flex items-center gap-1.5 min-h-[44px] px-6 rounded-[calc(var(--r)-4px)] text-sm font-semibold text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] hover:opacity-95 transition">
          Шалгах
        </button>
      </div>
    </div>
  );

  /* --- Амжилттай --- */
  const success = (
    <div className="flex flex-col items-center text-center gap-4 py-4">
      <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
        <IconCheck size={32} strokeWidth={3} />
      </div>
      <div className="flex flex-col gap-1">
        <div className="text-[18px] font-extrabold text-slate-900">Төлбөр амжилттай</div>
        <div className="text-[13px] text-slate-500">
          {car.plate} · {tug(paidTotal)} · {selected.key}
          {ebarimt === 'Байгууллага' ? ` · Байгууллага (РД ${rd})` : ' · Хувь хүн'}
        </div>
      </div>
      <FakeQR seed={paidTotal + car.plate.length * 7919} />
      <div className="text-[12px] text-slate-400">И-баримтын QR код</div>
      <button onClick={() => onFinalize(car, paidTotal)}
        className="inline-flex items-center justify-center gap-2 min-h-[48px] px-8 rounded-[var(--r)] text-[15px] font-semibold text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] shadow-md shadow-cyan-600/25 hover:opacity-95 active:scale-[0.98] transition">
        Дуусгах
      </button>
    </div>
  );

  return (
    <div className={'absolute inset-0 z-40 ' + (open ? '' : 'pointer-events-none')}>
      <div onClick={phase === 'form' ? onClose : undefined}
        style={{
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          transition: 'opacity .3s, visibility 0s linear ' + (open ? '0s' : '.3s'),
        }}
        className="absolute inset-0 bg-slate-900/40"></div>

      <div
        style={{
          transform: mobile
            ? (open ? 'translateY(0)' : 'translateY(100%)')
            : (open ? 'translate(-50%, -50%)' : 'translate(-50%, -46%)'),
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          transition: 'transform .3s ease-out, opacity .25s, visibility 0s linear ' + (open ? '0s' : '.3s'),
        }}
        className={
          'absolute bg-white shadow-2xl flex flex-col ' +
          (mobile
            ? 'inset-x-0 bottom-0 top-7 rounded-t-[20px]'
            : 'left-1/2 top-1/2 w-[820px] max-w-[94%] max-h-[92%] rounded-[calc(var(--r)+6px)]')
        }>
        {/* толгой */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="font-bold text-slate-900 text-[16px]">Төлбөр авах</div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition">
            <IconX size={17} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {phase === 'success' && success}
          {phase === 'qpay' && qpayView}
          {phase === 'form' && (
            <div className={mobile ? 'flex flex-col gap-5' : 'grid grid-cols-2 gap-5 h-full'}>
              {receipt}
              {payside}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
